import * as types from "./actionTypes";
import handleErrors from "./handleErrors";
import { addNotification } from "./notificationActions";
import getAPIPath from "../../js/modules/api-location";

export function getStripeCustomerDetailsSuccess(data) {
  return { type: types.GET_STRIPE_CUSTOMER_DETAILS_SUCCESS, data };
}

export function getStripeCustomerDetailsError() {
  return { type: types.GET_STRIPE_CUSTOMER_DETAILS_ERROR };
}

export function checkDiscountCodeIsValidSuccess(discountCode, data) {
  return {
    type: types.CHECK_DISCOUNT_CODE_IS_VALID_SUCCESS,
    discountCode,
    data,
  };
}

export function getPaymentHistorySuccess(data) {
  return { type: types.GET_STRIPE_CUSTOMER_PAYMENT_HISTORY_SUCCESS, data };
}

export function getStripeCustomerFuturePaymentSuccess(data) {
  return { type: types.GET_STRIPE_CUSTOMER_FUTURE_PAYMENT_SUCCESS, data };
}

export function getStripeCustomerDetails() {
  return function(dispatch) {
    return fetch(`${getAPIPath}commandapi/payments/customer-details`, {
      method: `GET`,
      credentials: `include`,
      headers: {
        Accept: `application/json`,
      },
    })
      .then((response) => {
        response.json().then((res) => {
          if (res.statusCode === 200) {
            dispatch(getStripeCustomerDetailsSuccess(res.data));
          } else {
            // If we can't get card details then we assume the user doesn't have a card.
            // This value is needed for the BillingDetailsPage componentWillReceiveProps function
            // to remove the loader.
            dispatch(getStripeCustomerDetailsError());
          }
        });
      })
      .catch((error) => {
        throw error;
      });
  };
}

export function checkDiscountCodeIsValid(discountCode) {
  return function(dispatch) {
    return fetch(
      `${getAPIPath}queryapi/payments/discount-check?discountcode=${discountCode}`,
      {
        method: `GET`,
        credentials: `include`,
        headers: {
          Accept: `application/json`,
        },
      },
    )
      .then((response) => {
        response.json().then((res) => {
          if (res.statusCode !== 401) {
            dispatch(checkDiscountCodeIsValidSuccess(discountCode, res.data));
          }
        });
      })
      .catch((error) => {
        throw error;
      });
  };
}

export function requestStripeToken(values) {
  return function(dispatch) {
    const {
      cardNumber,
      cvc,
      nameOnCard,
      expiryMonth,
      expiryYear,
      billingLine1,
      billingLine2,
      billingCity,
      billingState,
      billingZip,
      billingCountry,
    } = values;

    // We need to get the Stripe key before we can request a Stripe Token
    return dispatch(getStripeSecretKey()).then((key) => {
      return new Promise((resolve, reject) => {
        const stripe = Stripe(key);
        stripe.createToken(
          {
            number: cardNumber,
            cvc,
            name: nameOnCard,
            exp_month: expiryMonth,
            exp_year: expiryYear,
            address_line1: billingLine1,
            address_line2: billingLine2,
            address_city: billingCity,
            address_state: billingState,
            address_zip: billingZip,
            address_country: billingCountry,
          },
          (status, response) => {
            if (response.error) {
              dispatch(
                addNotification({
                  message: response.error.message,
                  level: `error`,
                  autoDismiss: 0,
                }),
              );
              reject();
            }
            return resolve(response.id);
          },
        );
      });
    });
  };
}

export function getStripeSecretKey() {
  return function(dispatch) {
    return fetch(`${getAPIPath}queryapi/key/stripe`, {
      method: `GET`,
      credentials: `include`,
      headers: {
        Accept: `application/json`,
      },
    })
      .then(handleErrors)
      .then((response) => {
        return response.json().then((res) => {
          return Promise.resolve(res.data);
        });
      })
      .catch(response =>
        response.json().then((res) => {
          dispatch(
            addNotification({
              message: res.message,
              level: `error`,
              autoDismiss: 0,
            }),
          );
          return Promise.reject(res);
        }),
      );
  };
}

export function getStripeCustomerPaymentHistory(cursor) {
  let paymentHistoryCursor = ``;
  if (cursor) {
    paymentHistoryCursor = `?more=${cursor}`;
  }

  return function(dispatch) {
    return fetch(
      `${getAPIPath}queryapi/payments/payment-history${paymentHistoryCursor}`,
      {
        method: `GET`,
        credentials: `include`,
        header: {
          Accept: `application/json`,
        },
      },
    )
      .then(handleErrors)
      .then(response =>
        response.json().then((res) => {
          dispatch(getPaymentHistorySuccess(res));
        }),
      )
      .catch(response =>
        response.json().then((res) => {
          dispatch(
            addNotification({
              message: res.message,
              level: `error`,
              autoDismiss: 0,
            }),
          );
          throw res;
        }),
      );
  };
}

export function getStripeCustomerFuturePayment() {
  return function(dispatch) {
    return fetch(`${getAPIPath}queryapi/payments/next-due-payment`, {
      method: `GET`,
      credentials: `include`,
      header: {
        Accept: `application/json`,
      },
    })
      .then(handleErrors)
      .then(response =>
        response.json().then((res) => {
          dispatch(getStripeCustomerFuturePaymentSuccess(res));
        }),
      )
      .catch(response =>
        response.json().then((res) => {
          dispatch(
            addNotification({
              message: res.message,
              level: `error`,
              autoDismiss: 0,
            }),
          );
          throw res;
        }),
      );
  };
}

export function updateCard() {
  const item = {};
  // item.plan = plan;
  // item.success_url = successUrl;
  // item.cancel_url = cancelUrl;

  return function(dispatch) {
    return fetch(`${getAPIPath}commandapi/payments/update-card`, {
      method: `POST`,
      body: JSON.stringify(item),
      credentials: `include`,
      headers: {
        Accept: `application/json`,
      },
    })
      .then((response) => {
        return handleErrors(response);
      })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        const sessionId = response.data || null;
        if (sessionId !== null) {
          return dispatch(getStripeSecretKey()).then((key) => {
            const stripe = Stripe(key);
            stripe.redirectToCheckout({ sessionId }).then(() => {
              // Checkout errors are passed to "then" not "catch"
            });
            dispatch(
              addNotification({
                message: response.message,
                level: `success`,
                autoDismiss: 8,
              }),
            );
          });
        }
        if (sessionId === null) {
          dispatch(
            addNotification({
              message: `sessionId === null`,
              level: `error`,
              autoDismiss: 8,
            }),
          );
        }
        return false;
      })
      .catch(response =>
        response.json().then((res) => {
          dispatch(
            addNotification({
              message: res.message,
              level: `error`,
              autoDismiss: 8,
            }),
          );
          throw res;
        }),
      );
  };
}



// WEBPACK FOOTER //
// ./library/app/actions/stripeActions.js